import React, { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useReorderActivity } from '../hooks/useCurriculum';
import { SortableActivityCard } from './SortableActivityCard';

/**
 * ActivityList - Displays list of activities with drag-drop reordering
 * Uses @dnd-kit for touch-compatible drag and drop on mobile devices
 */
export const ActivityList = ({
  activities,
  childId,
  newActivityPath,
  onDeleteClick,
  t
}) => {
  const reorderMutation = useReorderActivity(childId);
  const [isReordering, setIsReordering] = useState(false);

  // Configure sensors for both mouse/pointer and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // Long press delay for touch
        tolerance: 5, // Allow 5px movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get activity IDs for SortableContext
  const activityIds = useMemo(
    () => activities.map(a => a.id),
    [activities]
  );

  // Handle drag end - calculate new position and call API
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = activities.findIndex(a => a.id === active.id);
    const newIndex = activities.findIndex(a => a.id === over.id);

    if (oldIndex === newIndex) {
      return;
    }

    const activityId = active.id;

    // Use arrayMove to get the correct final order
    // This properly handles all cases (move up, move down, move to end)
    const newOrder = arrayMove(activities, oldIndex, newIndex);

    // Find the position of the moved item in the new array
    const movedItemNewIndex = newOrder.findIndex(a => a.id === activityId);

    // Calculate prev and next from the new order
    const prevActivityId = movedItemNewIndex > 0
      ? newOrder[movedItemNewIndex - 1].id
      : null;
    const nextActivityId = movedItemNewIndex < newOrder.length - 1
      ? newOrder[movedItemNewIndex + 1].id
      : null;

    setIsReordering(true);
    try {
      await reorderMutation.mutateAsync({
        activityId,
        prevActivityId,
        nextActivityId
      });
    } catch (err) {
      console.error('Failed to reorder activity:', err);
    } finally {
      setIsReordering(false);
    }
  };

  // Build edit activity path for React Router
  const getEditPath = (activityId) => {
    // newActivityPath is like /dashboard/children/1/activities/new
    // We need /dashboard/children/1/activities/:id/edit
    return newActivityPath.replace('/new', `/${activityId}/edit`);
  };

  return (
    <div className="card border rounded-3">
      <div className="card-body p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h3 className="h5 fw-bold mb-0">{t('curriculum.assigned_activities.title')}</h3>
          <Link to={newActivityPath} className="btn btn-success">
            <i className="bi bi-plus-lg me-2"></i>
            {t('curriculum.assigned_activities.add_activity')}
          </Link>
        </div>

        {/* Loading Spinner */}
        {isReordering && (
          <div className="mb-3 text-center">
            <div className="d-inline-flex align-items-center gap-2 text-primary">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="small">{t('curriculum.assigned_activities.updating_order')}</span>
            </div>
          </div>
        )}

        {/* Activities List */}
        {activities.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox" style={{ fontSize: '48px' }}></i>
            <p className="mt-3">{t('curriculum.assigned_activities.no_activities')}</p>
            <p className="small">{t('curriculum.assigned_activities.click_to_start')}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activityIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="d-flex flex-column gap-3">
                {activities.map((activity) => (
                  <SortableActivityCard
                    key={activity.id}
                    activity={activity}
                    editPath={getEditPath(activity.id)}
                    onDelete={() => onDeleteClick(activity)}
                    t={t}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ActivityList;
